import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Base64 } from 'js-base64';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { HttpParamNames } from 'src/app/core/constantes/http_param-names';
import { Attachment } from 'src/app/models/server/attachment';
import { Results } from 'src/app/models/server/core/results-T';
import { Folder } from 'src/app/models/server/folder';
import { FolderDocument } from 'src/app/models/server/folder-document';
import { FolderWorkflowStepsSnapshot } from 'src/app/models/server/folder-workflow-steps-snapshot';
import { IFolder } from 'src/app/models/server/interface/folder.interface';
import { StandByDatas } from 'src/app/models/server/stand-by-datas';
import { FilterInfos } from '../../models/server/core/filter-infos';
import { SortInfos } from '../../models/server/core/sort-infos';
import { ITchatRoom } from '../../models/server/interface/tchat-room.interface';
import { SavedTchatRoom } from '../../models/server/saved-tchat-room';
import { TchatRoom } from '../../models/server/tchat-room';
import { ConnectedUsersStateService } from '../state/connected-users-state.service';
import { TChatStateService } from '../state/tchat-state.service';
import { IFolderViewerService } from './folder-viewer-service.interface';
import { ServiceBase } from './service-base';

@Injectable({
    providedIn: 'root',
})
export class FoldersService extends ServiceBase implements IFolderViewerService {
    /**
     * Constructeur
     * */
    constructor(
        private http: HttpClient,
        public override connectedUserStateService: ConnectedUsersStateService,
        private tChatService: TChatStateService
    ) {
        super(connectedUserStateService);
    }

    /**
     * Recupère la liste des dossiers pour une vue specifique
     * @returns Retourne une liste de dossiers
     */
    public folderList(
        viewId: number,
        from: number,
        size: number,
        sort: SortInfos,
        filters: FilterInfos[],
        generalFilters: FilterInfos[],
        forSelection: boolean = false
    ): Observable<Results<Folder>> {
        let params = new HttpParams();
        params = params.set(HttpParamNames.FOR_SELECTION, forSelection);

        const httpOptions = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                from: from.toString(),
                size: size.toString(),
                sort: !!sort ? Base64.encode(JSON.stringify(sort)) : '',
                filters: !!filters ? Base64.encode(JSON.stringify(filters)) : '',
                generalFilters: !!generalFilters ? Base64.encode(JSON.stringify(generalFilters)) : '',
            }),
            params: params,
        };

        return this.http.get<Results<Folder>>(this.baseUrl + `/api/organizations/${this.organizationId}/views/${viewId}/folders`, httpOptions).pipe(
            map((x) => {
                var folderResult = Results.getCopyFrom<Folder>(Folder, x);

                // Initialise la proprité expanded à false
                folderResult.value.forEach((fr) => {
                    fr.expanded = false;
                    fr.viewId = viewId;
                });

                return folderResult;
            })
        );
    }

    /**
     * Exporte la liste des dossiers sous forme de fichier EXCEL.
     * @returns Retourne un fichier EXCEL.
     */
    public export(viewId: number, sort: SortInfos, filters: FilterInfos[], generalFilters: FilterInfos[]): Observable<Blob> {
        let params = new HttpParams();

        const httpOptions = {
            headers: new HttpHeaders({
                sort: !!sort ? Base64.encode(JSON.stringify(sort)) : '',
                filters: !!filters ? Base64.encode(JSON.stringify(filters)) : '',
                generalFilters: !!generalFilters ? Base64.encode(JSON.stringify(generalFilters)) : '',
            }),
            params: params,
            responseType: 'blob' as 'blob',
        };

        return this.http.get(this.baseUrl + `/api/organizations/${this.organizationId}/views/${viewId}/folders/export`, httpOptions).pipe(
            map((x) => {
                return x;
            })
        );
    }

    /** Retourne le nombre de dossier dans une banettes*/
    public folderBoxCount(boxId: string): Observable<number> {
        const httpOptions = {
            headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
        };

        return this.http.get<number>(this.baseUrl + `/api/organizations/${this.organizationId}/boxes/${boxId}/folders/count`, httpOptions);
    }

    /**
     * Récupère les informations d'un dossier
     * @param folderId: Id du dossier
     */
    public get(viewId: number, folderId: string): Observable<Folder> {
        const httpOptions = {
            headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
        };

        return this.http.get<IFolder>(this.baseUrl + `/api/organizations/${this.organizationId}/folders/${folderId}`, httpOptions).pipe(
            map((x) => {
                let folder = Folder.from(x);
                return folder;
            })
        );
    }

    /**
     * Récupère le lien de suivi d'un dossier.
     * @param folderId: Id du dossier
     */
    public getTrackingLink(folderId: string): Observable<string> {
        const httpOptions = {
            headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
        };

        return this.http.get<string>(this.baseUrl + `/api/organizations/${this.organizationId}/folders/${folderId}/trackingLink`, httpOptions);
    }

    /**
     * Ajout d'un dossier
     * @param folder: Information du dossier
     */
    public add(viewId: number, folder: Folder): Observable<Folder> {
        const httpOptions = {
            headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
        };

        return this.http.post<IFolder>(this.baseUrl + `/api/organizations/${this.organizationId}/views/${viewId}/folders?creationForm=true`, folder, httpOptions).pipe(
            map((x) => {
                let folder = Folder.from(x);
                return folder;
            })
        );
    }

    /**
     * Met à jour un attribut d'un dossier
     * @param folderViewId Id de la vue à laquelle appartient le dossier.
     * @param folderId: Identifiant du dossier
     * @param folder: Information du dossier
     * @param attributeDefinitionId: Id de la definition de l'attribut à mettre à jour.
     */
    public updateAttribute(folderViewId: number, folderId: string, folder: Folder, attributeDefinitionId: number): Observable<Folder> {
        const httpOptions = {
            headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
        };

        return this.http
            .put<IFolder>(
                this.baseUrl + `/api/organizations/${this.organizationId}/views/${folderViewId}/folders/${folderId}/attributes/${attributeDefinitionId}`,
                folder,
                httpOptions
            )
            .pipe(
                map((x) => {
                    let folder = Folder.from(x);
                    return folder;
                })
            );
    }

    /**
     * Déplace un dossier dans une bannette
     * @param folderViewId Id de la vue à laquelle appartient le dossier.
     * @param folderId: Identifiant du dossier
     * @param targetBoxId: Identifiant de la bannette de destination.
     */
    public moveToBox(folderViewId: number, folderId: string, targetBoxId: string): Observable<Folder> {
        const httpOptions = {
            headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
        };

        return this.http.put<IFolder>(this.baseUrl + `/api/organizations/${this.organizationId}/views/${folderViewId}/folders/${folderId}/box/${targetBoxId}`, httpOptions).pipe(
            map((x) => {
                let folder = Folder.from(x);
                return folder;
            })
        );
    }

    /**
     * Bascule l'état de lecture d'un dossier à lu ou non lu.
     * @param folderId: Identifiant du dossier
     * @param readStatus: Status de lecture..
     */
    public toggleReadStatus(viewId: number, folderId: string, readStatus: boolean): Observable<Folder> {
        const httpOptions = {
            headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
        };

        return this.http.put<IFolder>(this.baseUrl + `/api/organizations/${this.organizationId}/views/${viewId}/folders/${folderId}/read-status/${readStatus}`, httpOptions).pipe(
            map((x) => {
                let folder = Folder.from(x);
                return folder;
            })
        );
    }

    /**
     * Suppression logique d'un dossier
     * @param folderId: Identifiant du dossier
     * @param folder: Information du dossier
     */
    public delete(viewId: number, folderId: string): Observable<boolean> {
        const httpOptions = {
            headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
        };

        return this.http.delete(this.baseUrl + `/api/organizations/${this.organizationId}/folders/${folderId}`, httpOptions).pipe(
            map((x) => {
                return true;
            })
        );
    }

    /**
     * Met à jour une étape de workflow d'un dossier.
     * @param folderId : Identifiant du dossier
     * @param workflowStepId : Identifiant du l'étape du workflow
     * @param stateId : Identifiant de l'état
     */
    public updateWorkflowStepsSnapshot(viewId: number, folderId: string, workflowStepId: number, stateId: number): Observable<FolderWorkflowStepsSnapshot> {
        const httpOptions = {
            headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
        };

        return this.http
            .put<FolderWorkflowStepsSnapshot>(
                this.baseUrl + `/api/organizations/${this.organizationId}/views/${viewId}/folders/${folderId}/workflowStep/${workflowStepId}/state/${stateId}`,
                null,
                httpOptions
            )
            .pipe(
                map((x) => {
                    return x;
                })
            );
    }

    /**
     * Active une étape de workflow d'un dossier.
     * @param folderId : Identifiant du dossier
     * @param workflowStepId : Identifiant du l'étape du workflow
     */
    public activateFolderWorkflowStep(viewId: number, folderId: string, workflowStepId: number): Observable<FolderWorkflowStepsSnapshot> {
        const httpOptions = {
            headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
        };

        return this.http
            .put<FolderWorkflowStepsSnapshot>(
                this.baseUrl + `/api/organizations/${this.organizationId}/views/${viewId}/folders/${folderId}/workflowStep/${workflowStepId}/activate`,
                null,
                httpOptions
            )
            .pipe(
                map((x) => {
                    return x;
                })
            );
    }

    /**
     * Clôture une étape de workflow d'un dossier.
     * @param folderId : Identifiant du dossier
     * @param workflowStepId : Identifiant du l'étape du workflow
     */
    public completeFolderWorkflowStep(viewId: number, folderId: string, workflowStepId: number): Observable<FolderWorkflowStepsSnapshot> {
        const httpOptions = {
            headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
        };

        return this.http
            .put<FolderWorkflowStepsSnapshot>(
                this.baseUrl + `/api/organizations/${this.organizationId}/views/${viewId}/folders/${folderId}/workflowStep/${workflowStepId}/complete`,
                null,
                httpOptions
            )
            .pipe(
                map((x) => {
                    return x;
                })
            );
    }

    /**
     * Récupère la liste des étapes de workflow d'un dossier.
     * @param viewId  : Identifiant de la vue.
     * @param folderId : Identifiant du dossier
     */
    public getFolderWorkflowStepSnapshots(viewId: number, folderId: string): Observable<FolderWorkflowStepsSnapshot[]> {
        const httpOptions = {
            headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
        };

        return this.http
            .get<FolderWorkflowStepsSnapshot[]>(this.baseUrl + `/api/organizations/${this.organizationId}/views/${viewId}/folders/${folderId}/workflowSteps`, httpOptions)
            .pipe(
                map((x) => {
                    return x;
                })
            );
    }

    public merge(viewId: number, sourceFolderId: string, destinationFolderId: string): Observable<Folder> {
        const httpOptions = {
            headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
        };

        return this.http
            .put<IFolder>(this.baseUrl + `/api/organizations/${this.organizationId}/views/${viewId}/folders/${sourceFolderId}/mergeto/${destinationFolderId}`, null, httpOptions)
            .pipe(
                map((x) => {
                    let folder = Folder.from(x);
                    return folder;
                })
            );
    }

    /**
     * Crée des copies d'un dossier
     * @param sourceFolderId : Id du dossier source.
     * @param duplicateCount : Nombre de copie à réaliser.
     * @returns : Le dossier source
     */
    public duplicate(viewId: number, sourceFolderId: string, duplicateCount: number): Observable<Folder> {
        const httpOptions = {
            headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
        };

        return this.http
            .post<IFolder>(this.baseUrl + `/api/organizations/${this.organizationId}/views/${viewId}/folders/${sourceFolderId}/clone/${duplicateCount}`, null, httpOptions)
            .pipe(
                map((x) => {
                    let folder = Folder.from(x);
                    return folder;
                })
            );
    }

    /**
     * Recupère le document au format strem
     * @param viewId Identifiant de la vue
     * @param folderId Identifiant du dossier
     * @param documentId Identificant du document
     * @param attachmentId Identifiant du document
     * @returns retourne un blob
     */
    public download(viewId: number, folderId: string, documentId: string, attachmentId: string): Observable<Blob> {
        const httpOptions = { responseType: 'blob' as 'blob' };

        return this.http.get(this.baseUrl + `/api/organizations/${this.organizationId}/folders/${folderId}/documents/${documentId}/attachmentid/${attachmentId}`, httpOptions).pipe(
            map((response) => {
                return response;
            })
        );
    }

    /**
     * Récupère la conversation.
     * @param folderId Identifiant du dossier
     * @param documentId Identificant du document
     * @param room Nom de la conversation
     * @returns Retourne une conversation.
     */
    public room(folderId: string, documentId: string, room: string): Observable<TchatRoom> {
        const httpOptions = {
            headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
        };

        return this.http.get<ITchatRoom>(this.baseUrl + `/api/organizations/${this.organizationId}/folders/${folderId}/documents/${documentId}/room/${room}`, httpOptions).pipe(
            map((x) => {
                let room = TchatRoom.createFormCopy(x);
                room.target = this.tChatService.getRoomTarget(x);
                return room;
            })
        );
    }

    /**
     * Sauvegarde une conversation tchat
     * @param folderId Identifiant du dossier
     * @param room Information de la conversation
     */
    public saveTchat(viewId: number, folderId: string, room: SavedTchatRoom): Observable<FolderDocument> {
        const httpOptions = {
            headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
        };
        return this.http.post<FolderDocument>(this.baseUrl + `/api/organizations/${this.organizationId}/views/${viewId}/folders/${folderId}/tchat`, room, httpOptions).pipe(
            map((x) => {
                x.viewId = viewId;
                return x;
            })
        );
    }

    /**
     * Vérouillage d'un dossier
     * @param folderId : Identifiant du dossier
     */
    public lock(viewId: number, folderId: string): Observable<boolean> {
        const httpOptions = {
            headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
        };
        return this.http.put(this.baseUrl + `/api/organizations/${this.organizationId}/views/${viewId}/folders/${folderId}/lock`, null, httpOptions).pipe(
            map((x) => {
                return true;
            })
        );
    }

    /**
     * Dévérouillage d'un dossier
     * @param folderId : Identifiant du dossier
     */
    public unlock(viewId: number, folderId: string): Observable<boolean> {
        const httpOptions = {
            headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
        };
        return this.http.put(this.baseUrl + `/api/organizations/${this.organizationId}/views/${viewId}/folders/${folderId}/unlock`, null, httpOptions).pipe(
            map((x) => {
                return true;
            })
        );
    }

    /**
     * Demande de dévérouillage d'un dossier
     * @param folderId : Identifiant du dossier
     */
    public requestUnlockFolder(viewId: number, folderId: string): Observable<boolean> {
        const httpOptions = {
            headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
        };
        return this.http.put(this.baseUrl + `/api/organizations/${this.organizationId}/views/${viewId}/folders/${folderId}/requestUnlock`, null, httpOptions).pipe(
            map((x) => {
                return true;
            })
        );
    }

    /**
     * Réponse négative à une demande de dévérouillage d'un dossier
     * @param folderId : Identifiant du dossier
     * @param requestorLogin : Login du demandeur de la demande de déverrouillage
     */
    public declineRequestUnlockFolder(viewId: number, folderId: string, requestorLogin: string): Observable<boolean> {
        const httpOptions = {
            headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
        };
        return this.http
            .put(this.baseUrl + `/api/organizations/${this.organizationId}/views/${viewId}/folders/${folderId}/requestUnlock/${requestorLogin}/decline`, null, httpOptions)
            .pipe(
                map((x) => {
                    return true;
                })
            );
    }

    /**
     * Dévérouillage d'un dossier
     * @param folderId : Identifiant du dossier
     * @param requestorLogin : Login du demandeur de la demande de déverrouillage
     */
    public acceptRequestUnlockFolder(viewId: number, folderId: string, requestorLogin: string): Observable<boolean> {
        const httpOptions = {
            headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
        };
        return this.http
            .put(this.baseUrl + `/api/organizations/${this.organizationId}/views/${viewId}/folders/${folderId}/requestUnlock/${requestorLogin}/accept`, null, httpOptions)
            .pipe(
                map((x) => {
                    return true;
                })
            );
    }

    /**
     * Place le dossier en veille (stand-by).
     * @param folderId: Id du dossier.
     * @param standByDatas: Données du mode veille.
     */
    public setInStandByState(viewId: number, folderId: string, standByDatas: StandByDatas): Observable<Folder> {
        const httpOptions = {
            headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
        };

        return this.http.patch<IFolder>(this.baseUrl + `/api/organizations/${this.organizationId}/views/${viewId}/folders/${folderId}/standby`, standByDatas, httpOptions).pipe(
            map((x) => {
                let folder = Folder.from(x);
                return folder;
            })
        );
    }

    /**
     * Retire le dossier du mode stand-by.
     * @param folderId: Id du dossier.
     */
    public removeFromStandByState(viewId: number, folderId: string): Observable<Folder> {
        const httpOptions = {
            headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
        };

        return this.http.delete<IFolder>(this.baseUrl + `/api/organizations/${this.organizationId}/views/${viewId}/folders/${folderId}/standby`, httpOptions).pipe(
            map((x) => {
                let folder = Folder.from(x);
                return folder;
            })
        );
    }

    /**
     * Passe le dossier en attente d'archivage.
     * @param viewId : Id de la vue.
     * @param folderId: Id du dossier.
     */
    public SetInWaitingArchivingState(viewId: number, folderId: string): Observable<Folder> {
        const httpOptions = {
            headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
        };

        return this.http.patch<IFolder>(this.baseUrl + `/api/organizations/${this.organizationId}/views/${viewId}/folders/${folderId}/archiving`, null, httpOptions).pipe(
            map((x) => {
                let folder = Folder.from(x);
                return folder;
            })
        );
    }

    /**
     * Retire le dossier de l'état en attente d'archivage.
     * @param folderId: Id du dossier.
     */
    public removeFromWaitingArchivingState(viewId: number, folderId: string): Observable<Folder> {
        const httpOptions = {
            headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
        };

        return this.http.delete<IFolder>(this.baseUrl + `/api/organizations/${this.organizationId}/views/${viewId}/folders/${folderId}/archiving`, httpOptions).pipe(
            map((x) => {
                let folder = Folder.from(x);
                return folder;
            })
        );
    }

    /**
     * Archive le dossier.
     * @param viewId : Id de la vue.
     * @param folderId: Id du dossier.
     */
    public archive(viewId: number, folderId: string): Observable<boolean> {
        const httpOptions = {
            headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
        };

        return this.http.post<IFolder>(this.baseUrl + `/api/organizations/${this.organizationId}/views/${viewId}/archives/${folderId}`, null, httpOptions).pipe(
            map((x) => {
                return true;
            })
        );
    }

    /**
     * Récupère les fichiers d'un dossier
     * @param folderId: Id du dossier
     */
    public getFolderFiles(folderId: string): Observable<Attachment[]> {
        const httpOptions = {
            headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
        };

        return this.http.get<Attachment[]>(this.baseUrl + `/api/organizations/${this.organizationId}/folders/${folderId}/attachments`, httpOptions);
    }

    /**
     * Récupère les fichiers d'un dossier
     * @param viewId: Id de la vue
     * @param folderId: Id du dossier
     */
    public followFolder(viewId: number, folderId: string): Observable<boolean> {
        const httpOptions = {
            headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
        };

        return this.http.put(this.baseUrl + `/api/organizations/${this.organizationId}/views/${viewId}/folders/${folderId}/follow`, httpOptions).pipe(
            map((x) => {
                return true;
            })
        );
    }

    /**
     * Récupère les fichiers d'un dossier
     * @param viewId: Id de la vue
     * @param folderId: Id du dossier
     */
    public stopFollowFolder(viewId: number, folderId: string): Observable<boolean> {
        const httpOptions = {
            headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
        };

        return this.http.put(this.baseUrl + `/api/organizations/${this.organizationId}/views/${viewId}/folders/${folderId}/stopFollow`, httpOptions).pipe(
            map((x) => {
                return true;
            })
        );
    }

    /**
     * Retourne la liste des dossiers suivi par l'utilisateur
     * @returns liste des dossiers suivi par l'utilisateur
     */
    public getUserFollowedFolders(from: number, size: number, sort: SortInfos, filters: FilterInfos[], generalFilters: FilterInfos[]): Observable<Results<Folder>> {
        const httpOptions = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                from: from.toString(),
                size: size.toString(),
                sort: !!sort ? Base64.encode(JSON.stringify(sort)) : '',
                filters: !!filters ? Base64.encode(JSON.stringify(filters)) : '',
                generalFilters: !!generalFilters ? Base64.encode(JSON.stringify(generalFilters)) : '',
            }),
        };

        return this.http.get<Results<Folder>>(
            this.baseUrl + `/api/organizations/${this.userConnected.organization.id}/users/${this.userConnected.userId}/followedFolders`,
            httpOptions
        );
    }
}
